import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import Axios from 'axios';
import { toast } from 'sonner';

import { setToken } from '~entities/studios/model/token-store';
import type { LoginResponse } from '~entities/users/model/types';
import { setCurrentUser } from '~entities/users/model/user-store';
import { baseAPI } from '~shared/api/base-api';
import type { ErrorWithMessage } from '~shared/api/types/error-with-message';
import { completeStandaloneStudioLogin } from '~shared/utils/complete-standalone-studio-login';
import { isStandaloneDevEnvironment } from '~shared/utils/is-standalone-dev-environment';

type UserLoginInput = {
  email: string;
  password: string;
  locationId: string;
  redirect?: string;
};

export const userLoginAPI = async (
  email: string,
  password: string,
  locationId: string,
) => {
  const response = await baseAPI.post<LoginResponse>(`/auth/user-login`, {
    email,
    password,
    locationId,
  });
  return response.data;
};

const navigateAfterLogin = async (params: {
  locationId: string;
  redirect?: string;
  navigate: ReturnType<typeof useNavigate>;
}) => {
  const { locationId, redirect, navigate } = params;

  if (isStandaloneDevEnvironment()) {
    const destination = await completeStandaloneStudioLogin(
      locationId,
      redirect,
    );
    void navigate(destination);
    return;
  }

  void navigate({
    to: '/ghl-authorize',
    search: { locationId, redirect },
  });
};

export const useUserLogin = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ email, password, locationId }: UserLoginInput) =>
      userLoginAPI(email, password, locationId),
    onSuccess: async (data, { locationId, redirect }) => {
      if (data.user.isFirstLogin) {
        localStorage.removeItem('isFirstVisit');
      }
      setToken(data.token);
      setCurrentUser(data.user);

      toast.success(`Welcome, ${data.user.firstName}`);

      try {
        await navigateAfterLogin({ locationId, redirect, navigate });
      } catch {
        toast.error('Signed in, but failed to load studio. Please try again.');
      }
    },
    onError: (error) => {
      if (Axios.isAxiosError(error)) {
        const errorMessage =
          (error.response?.data as ErrorWithMessage).error?.message ??
          'Failed to login';
        return toast.error(errorMessage);
      }
      return toast.error('Failed to login');
    },
  });
};
